/**
 * F60 — one log line in full: key facts + the redacted JSON (or raw text),
 * with copy and "show this request" (filters the search by its request id).
 */
import { useI18n } from '@bravemove/i18n/react';
import type { OpsLogEvent } from '@bravemove/types';
import { Badge, Button, Drawer, useToast } from '@bravemove/ui-web';
import { formatLogTime, levelTone, roundMs, statusTone, type TimeZoneMode } from '../opsHelpers';

export interface LogDetailDrawerProps {
  readonly event: OpsLogEvent | null;
  readonly timeZone: TimeZoneMode;
  readonly onClose: () => void;
  readonly onShowRequest: (requestId: string) => void;
}

export function LogDetailDrawer({ event, timeZone, onClose, onShowRequest }: LogDetailDrawerProps) {
  const { t, locale } = useI18n();
  const { push } = useToast();
  const body = event ? (event.data ? JSON.stringify(event.data, null, 2) : (event.raw ?? '')) : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      push({ tone: 'success', title: t('admin:opsLogs.detail.copied') });
    } catch {
      push({ tone: 'danger', title: t('admin:opsLogs.detail.copyFailed') });
    }
  };

  const facts: ReadonlyArray<[string, string | null]> = event
    ? [
        ['time', formatLogTime(event.timestamp, timeZone, locale, true)],
        ['method', event.method],
        ['route', event.route ?? event.url],
        [
          'responseTime',
          event.responseTime === null
            ? null
            : t('admin:opsLogs.units.ms', { value: roundMs(event.responseTime) }),
        ],
        ['requestId', event.reqId],
        ['userId', event.userId],
        ['adminId', event.adminId],
        ['event', [event.event, event.action].filter(Boolean).join(' · ') || null],
        ['error', event.error],
      ]
    : [];

  return (
    <Drawer
      open={event !== null}
      onClose={onClose}
      title={event?.msg ?? t('admin:opsLogs.detail.rawTitle')}
      width={560}
      closeLabel={t('admin:opsLogs.detail.close')}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {event?.reqId ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onShowRequest(event.reqId as string)}
              data-testid="ops-log-detail-show-request"
            >
              {t('admin:opsLogs.detail.showRequest')}
            </Button>
          ) : null}
          <Button variant="primary" size="sm" icon="copy" onClick={() => void copy()}>
            {t('admin:opsLogs.detail.copy')}
          </Button>
        </div>
      }
    >
      {event ? (
        <div className="space-y-4" data-testid="ops-log-detail">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={levelTone(event.level)}>
              {event.level ? t(`admin:opsLogs.level.${event.level}`) : t('admin:opsLogs.level.raw')}
            </Badge>
            {event.statusCode !== null ? (
              <Badge tone={statusTone(event.statusCode)}>{event.statusCode}</Badge>
            ) : null}
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-small">
            {facts.map(([key, value]) =>
              value ? (
                <div key={key} className="contents">
                  <dt className="text-ink-3">{t(`admin:opsLogs.detail.fields.${key}`)}</dt>
                  <dd className="min-w-0 break-all font-mono text-ink">{value}</dd>
                </div>
              ) : null,
            )}
          </dl>
          <pre
            className="max-h-[55vh] overflow-auto whitespace-pre-wrap break-all rounded-md border border-hairline bg-surface-2 p-3 font-mono text-[12px] text-ink"
            data-testid="ops-log-detail-json"
          >
            {body}
          </pre>
          <p className="text-[12px] text-ink-3">{t('admin:opsLogs.detail.redactionNote')}</p>
        </div>
      ) : null}
    </Drawer>
  );
}
